
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
/* eslint-disable @next/next/no-img-element */
import { GetServerSideProps } from 'next/types';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { AiOutlineSetting } from 'react-icons/ai';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import Layout from '@/components/layout/Layout';
import Seo from '@/components/Seo';
import NotFoundPage from '@/pages/404';
import {
  getProductSlug,
  isProductAvailable,
} from '@/lib/data-transformer/productData';
import { useProduct } from '@/lib/hooks/useProduct';
import { ProductItem } from '@/lib/types/api';
import AccountManagerForm from '@/components/forms/AccountManagerForm';
import {
  ProductHero,
  ProductAdditionalInformation,
  ProductPaymentTerms,
  ProductDescription,
  ProductBreadCrumbs,
  ProductDocuments,
  ProductAdditionalEquipment,
  RelatedProducts,
  ProductOverview,
} from '@/components/product';
import { TechnicalInformation } from '@/components/product/TechnicalInformation';
import dynamic from 'next/dynamic';
import {
  fetchAccountManager,
  fetchProductData,
  fetchRelatedProducts,
} from '@/lib/query-client/query-fetcher';
import { Disclaimer } from '@/components/product/Disclaimer';
import { ProductPageSidebar } from '@/components/product/ProductPageSidebar';

// Components
export const ProductGallery = dynamic(
  () => import('@/components/visualization/ProductGallery'),
  {
    ssr: false,
  }
);

export default function ProductPage() {
  const { t } = useTranslation('products');
  const { isFallback } = useRouter();
  const { product, isLoading } = useProduct();

  if (isLoading || isFallback) {
    return (
      <Layout isNavbarTransparent={true}>
        <main className='mx-auto h-screen w-full bg-brand-800'>
          <div className='flex h-full flex-col items-center justify-center space-y-4'>
            <AiOutlineSetting
              className='h-20 w-20 animate-[spin_3s_ease-in-out_infinite] text-white'
              data-testid='loading-icon'
            />
          </div>
        </main>
      </Layout>
    );
  }

  if (!product) {
    return <NotFoundPage />;
  }

  const isAvailable = isProductAvailable(product);

  return (
    <Layout isNavbarTransparent={true}>
      <main>
        <Seo
          machineId={getProductSlug(product)}
          title={product.seo_title}
          description={product.seo_description}
          keywords={t('seo:PRODUCT_DETAIL-keywords')}
          image={product.thumbnail.thumbnail_url}
          product={product}
        />

        <ProductHero product={product} />

        <div className='mx-auto'>
          <ProductPageSidebar product={product} isAvailable={isAvailable} />
          <MainContent product={product} />
        </div>

        <RelatedProducts product={product} />
      </main>
    </Layout>
  );
}

function MainContent({ product }: { product: ProductItem }) {
  const { t } = useTranslation();
  return (
    <div className='w-full child:min-[1400px]:mr-[44%] child:min-[1400px]:pl-40'>
      <ProductBreadCrumbs product={product} />
      <ProductDescription product={product} />
      <ProductOverview product={product} />
      <TechnicalInformation product={product} />
      <ProductAdditionalEquipment product={product} />
      <ProductAdditionalInformation product={product} />
      <Disclaimer message={t('products:PRODUCT_DETAIL_INFO_DISCLAIMER')} />
      <ProductDocuments product={product} />
      <AccountManagerForm product={product} />
      <ProductPaymentTerms />
    </div>
  );
}

 const _getServerSideProps: GetServerSideProps = async ({
  params,
  locale,
}) => {
  const queryClient = new QueryClient();
  const machineSku = params?.machine_id?.toString().split('_')[1];

  if (!machineSku) {
    console.warn(
      `Machine SKU not found in params: ${JSON.stringify(
        params?.machine_id
      )} sku: ${machineSku}`
    );
    return { notFound: true };
  }

  try {
    await fetchProductData(queryClient, machineSku, locale);
    await fetchAccountManager(queryClient, locale);
    await fetchRelatedProducts(queryClient, machineSku, locale);

    const product: ProductItem | undefined = queryClient.getQueryData([
      'product',
      machineSku,
      locale,
    ]);
    return {
      props: { dehydratedState: dehydrate(queryClient) },
      notFound: !product,
    };
  } catch (error) {
    console.error(`Error in getServerSideProps for SKU: ${machineSku}`, error);
    return { props: {} };
  }
};


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/product/[machine_id]/index',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  